import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'primereact/checkbox'
import FormDialog from '../../../components/ui/FormDialog'
import MobileSheetDropdown from '../../../components/ui/MobileSheetDropdown'
import { playClickSound } from '../../../lib/sound'
import {
  PRIORITY_REMAINING,
  remainingBound,
  type PriorityCriteria,
  type PriorityRemaining,
} from '../types'

interface Props {
  visible: boolean
  /** Gönderim sürüyor mu? (birincil buton yükleniyor — çift gönderim olmasın) */
  submitting: boolean
  onSubmit: (criteria: PriorityCriteria) => void
  onHide: () => void
}

/**
 * **"Görevleri Önceliklendir"** penceresi (kullanıcı isteği 2026-10-21). Buton artık filtrelenmiş
 * kümenin tamamına bildirim üretmez; Genel Müdür burada hedefi seçer:
 *
 * <ul>
 *   <li><b>Kalan Süre</b> (solda, tek seçimli combo): 1 / 2 / 3 / 4 / 5 / 10 / 20 / 30 gün → hedef teslim
 *       tarihine <b>0..N</b> gün kalanlar; "30 Günden Fazla" → 31 ve üzeri. Gecikenleri <b>kapsamaz</b>.
 *   <li><b>Geciken Görevler</b> (sağda, onay kutusu): hedef teslim tarihi geçmiş açık görevler.
 * </ul>
 *
 * <p>İkisi birlikte seçilirse kümeler <b>birleşir</b> ("5 gün + Geciken" = 0–5 gün kalanlar ∪ bütün
 * gecikenler); yalnız biri de seçilebilir. Hiçbiri seçili değilse gönderim butonu pasiftir — "hepsine
 * gönder" yolu sunucuda da kapalıdır. Pencerenin altındaki özet satırı seçimin <b>neyi</b> kapsadığını
 * cümleyle söyler; birleşim/kesişim ayrımını kullanıcıya bırakmamak için.
 *
 * <p><b>Neden combo:</b> ilk sürümde üç radyo düğmesiydi; talep revizyonu seçenekleri dokuza çıkardı ve
 * seçiciyi "gün combosu" diye andı. Seçim combo'nun kendi temizleme düğmesiyle (×) kaldırılır — "yalnız
 * gecikenler"e dönmek için pencereyi kapatıp açmak gerekmez.
 *
 * <p>Pencere her açılışta <b>boş</b> başlar: önceki gönderimin seçimi bir sonrakine taşınsaydı kullanıcı
 * farkında olmadan aynı kümeye yeniden bildirim gönderebilirdi.
 *
 * <p><b>Tıklama sesi "Bildirim Gönder"de çalar</b>, pencereyi açan butonda değil (kullanıcı isteği
 * 2026-10-26): ses gönderimin geri bildirimidir — pencere açılırken henüz hiçbir şey gönderilmemiştir.
 */
export default function PrioritizeTasksDialog({
  visible,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const [remaining, setRemaining] = useState<PriorityRemaining | null>(null)
  const [overdue, setOverdue] = useState(false)

  useEffect(() => {
    if (visible) {
      setRemaining(null)
      setOverdue(false)
    }
  }, [visible])

  const empty = remaining == null && !overdue

  // "N Gün" etiketi `count` ile çağrılır: İngilizcede tekil/çoğul ayrışır ("1 Day" / "2 Days"); "30 Günden
  // Fazla" hep 30 olduğu için çoğullama gerektirmez.
  const label = (key: PriorityRemaining) => {
    const { days, over } = remainingBound(key)
    return over ? t('home.prioritize.daysOver', { days }) : t('home.prioritize.days', { count: days })
  }
  const options = PRIORITY_REMAINING.map((key) => ({ value: key, label: label(key) }))

  const summary = () => {
    if (remaining == null) {
      return t(overdue ? 'home.prioritize.summaryOverdue' : 'home.prioritize.summaryEmpty')
    }
    const { days, over } = remainingBound(remaining)
    const target = t(over ? 'home.prioritize.targetOver' : 'home.prioritize.targetRange', { days })
    return t(overdue ? 'home.prioritize.summaryBoth' : 'home.prioritize.summaryRemaining', { target })
  }

  return (
    <FormDialog
      visible={visible}
      header={t('home.remind.all')}
      submitLabel={t('home.prioritize.submit')}
      submitting={submitting}
      submitDisabled={empty}
      onSubmit={() => {
        playClickSound()
        onSubmit({ remaining, overdue })
      }}
      onHide={onHide}
    >
      <p className="home-prioritize-intro">{t('home.prioritize.intro')}</p>
      <div className="home-prioritize-grid">
        <fieldset className="home-prioritize-group">
          <legend>{t('home.prioritize.remaining')}</legend>
          <MobileSheetDropdown
            className="home-prioritize-remaining"
            value={remaining}
            options={options}
            onChange={(e) => setRemaining((e.value as PriorityRemaining | undefined) ?? null)}
            placeholder={t('home.prioritize.remainingPlaceholder')}
            aria-label={t('home.prioritize.remaining')}
            showClear
          />
        </fieldset>
        <fieldset className="home-prioritize-group">
          <legend>{t('home.prioritize.overdueGroup')}</legend>
          <span className="home-prioritize-option">
            <Checkbox
              inputId="prioritize-overdue"
              checked={overdue}
              onChange={(e) => setOverdue(!!e.checked)}
            />
            <label htmlFor="prioritize-overdue">{t('home.prioritize.overdue')}</label>
          </span>
        </fieldset>
      </div>
      <p className="home-prioritize-summary" aria-live="polite">
        {summary()}
      </p>
    </FormDialog>
  )
}
